import { Button } from '@/shared/ui/button';
import { Plus, Trash, User } from 'lucide-react';
import EmptyView from '@/shared/components/EmptyView';
import { useState } from 'react';
import { ServiceLine, ServiceLineStatus } from '@/shared/types/serviceLineTypes';
import AddCustomerToServiceLineDialog from '@/domains/service-lines/dialogs/AddCustomerToServiceLineDialog';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';
import { useRemoveCustomerFromServiceLine } from '@/domains/service-lines/services/account-service-lines';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';

interface CustomerSectionProps {
  serviceLine: ServiceLine;
  refetchServiceLine: () => void;
}

export const CustomerSection = ({
  serviceLine,
  refetchServiceLine
}: CustomerSectionProps) => {
  const [isAddCustomerToServiceLineDialogOpen, setIsAddCustomerToServiceLineDialogOpen] = useState(false);
  const [isRemoveCustomerFromServiceLineDialogOpen, setIsRemoveCustomerFromServiceLineDialogOpen] = useState(false);

  const removeCustomerFromServiceLine = useRemoveCustomerFromServiceLine();

  const customer = serviceLine?.customer ?? null;

  const handleAddCustomerToServiceLine = () => {
    setIsAddCustomerToServiceLineDialogOpen(true);
  };

  const handleRemoveCustomerFromServiceLine = () => {
    setIsRemoveCustomerFromServiceLineDialogOpen(true);
  };

  const confirmRemoveCustomerFromServiceLine = async () => {
    if (!serviceLine?.customer?.customerId || !serviceLine?.serviceLineNumber) return;

    try {
      await removeCustomerFromServiceLine.mutateAsync({
        serviceLineNumber: serviceLine.serviceLineNumber,
        customerId: serviceLine.customer.customerId
      });
      showSuccess(SUCCESS.SERVICE_LINE.REMOVED_CUSTOMER);
      refetchServiceLine();
      setIsRemoveCustomerFromServiceLineDialogOpen(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_LINE.REMOVE_CUSTOMER);
    }
  };
  return (
    <div className="border rounded-lg">
      <div className="flex items-center justify-between bg-muted p-4 rounded-t-lg">
        <div className="flex items-center gap-2">
          <User className="h-6 w-6" />
          <h2 className="text-2xl font-bold">Associated Customer</h2>
        </div>
        {customer && customer.customerId ? (
          null
        ) : (
          <>
            {handleAddCustomerToServiceLine && (
              <Button
                variant="default"
                size="default"
                className="gap-2"
                onClick={handleAddCustomerToServiceLine}
                disabled={serviceLine.updateAllowed === false || serviceLine.status === ServiceLineStatus.SUSPENDED}
              >
                <Plus className="h-4 w-4" />
                Add Customer
              </Button>
            )}
          </>
        )}
      </div>
      <div className="p-4">
        {customer && customer.customerId ? (
          <div key={customer.customerId} className="flex items-center justify-between border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <p className="text-sm">{customer.name}</p>
            </div>
            {handleRemoveCustomerFromServiceLine && (
              <Button
                variant="destructive"
                size="default"
                onClick={handleRemoveCustomerFromServiceLine}
                className="gap-2"
                disabled={serviceLine.updateAllowed === false || serviceLine.status === ServiceLineStatus.SUSPENDED}
              >
                <Trash className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : (
          <EmptyView
            icon={User}
            size="medium"
            title="No Customer Assigned"
            description="This service line doesn't have any customer assigned to it yet."
          />
        )}
      </div>

      {/* Add Customer to Service Line Dialog */}
      {
        serviceLine?.serviceLineNumber && (
          <AddCustomerToServiceLineDialog
            open={isAddCustomerToServiceLineDialogOpen}
            onOpenChange={setIsAddCustomerToServiceLineDialogOpen}
            onSuccess={refetchServiceLine}
            serviceLine={serviceLine}
          />
        )
      }

      {/* Remove Customer from Service Line Dialog */}
      <GenericAlertDialog
        open={isRemoveCustomerFromServiceLineDialogOpen}
        onOpenChange={setIsRemoveCustomerFromServiceLineDialogOpen}
        title="Remove Customer from Service Line"
        description="Are you sure you want to remove this customer from the service line?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={confirmRemoveCustomerFromServiceLine}
        loading={removeCustomerFromServiceLine.isPending}
      />
    </div >
  );
}; 